<script lang="ts">
import { ref, toRaw, type Ref } from "vue";

interface PaymentAddressFlags {
  tag?: string;
  billingAddressEnabled?: boolean;
}

const billingSameAsShippingByPayment = new WeakMap<object, Ref<boolean>>();

const getBillingSameAsShippingState = (
  payment: PaymentAddressFlags
): Ref<boolean> => {
  const paymentKey = toRaw(payment);
  const existingState = billingSameAsShippingByPayment.get(paymentKey);
  if (existingState) return existingState;

  const state = ref(true);
  billingSameAsShippingByPayment.set(paymentKey, state);
  return state;
};
</script>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { hasGroupAddress } from "@/util/sellProductAddress";
import { UICheckbox } from "@gohighlevel/ghl-ui";
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import BillingAddressPreview from "./BillingAddressPreview.vue";

const props = defineProps({
  payment: {
    type: Object as PropType<PaymentAddressFlags>,
    required: true,
  },
  inputClass: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const store = useAppStore();
const { t } = useI18n();
const billingSameAsShipping = computed({
  get: () => getBillingSameAsShippingState(props.payment).value,
  set: (value: boolean) => {
    getBillingSameAsShippingState(props.payment).value = value;
  },
});
const billingEnabled = computed(
  () =>
    store.payment.paymentType === "product" &&
    props.payment.billingAddressEnabled === true &&
    hasGroupAddress(store.sellProductAddressSlides)
);
</script>

<template>
  <div
    v-if="billingEnabled"
    class="flex flex-col gap-4 self-stretch"
    data-testid="payment-billing-address-prelude"
  >
    <UICheckbox
      id="billing-same-as-shipping"
      data-testid="billing-same-as-shipping"
      :checked="billingSameAsShipping"
      :aria-label="t('common.sellProductsAddress.billingSameAsShipping')"
      @update:checked="billingSameAsShipping = $event"
    >
      {{ t("common.sellProductsAddress.billingSameAsShipping") }}
    </UICheckbox>
    <BillingAddressPreview v-if="!billingSameAsShipping" :input-class="inputClass" />
  </div>
</template>
